export const formatDateBR = (value: string) => {
  const [year, month, day] = value.split('-')
  if (!year || !month || !day) return value
  return `${day}/${month}/${year}`
}

type TDeliveryEstimateRange = { range_start?: string | null; range_end?: string | null } | null | undefined

export function getSplitDeliveryEstimateTexts(params: {
  hasPillItem: boolean
  isAllPillItems: boolean
  pillEstimate: TDeliveryEstimateRange
  othersEstimate: TDeliveryEstimateRange
}): string[] | null {
  const { hasPillItem, isAllPillItems, pillEstimate, othersEstimate } = params
  if (!hasPillItem) return null

  // Nenhuma tentativa de estimativa ainda (CEP não informado) — nenhum dos dois lados tem sequer o
  // objeto de estimativa. Mantém o fallback genérico dos chamadores (PILL_DELIVERY_CEP_TEXT) em vez
  // de afirmar "indisponível"/"calculando" precipitadamente.
  if (!pillEstimate && !othersEstimate) return null

  const pillHasRange = !!pillEstimate?.range_start && !!pillEstimate?.range_end
  const pillText = pillHasRange
    ? `Medicamentos (Vendido por Pill): entrega estimada entre ${formatDateBR(
        pillEstimate!.range_start as string
      )} e ${formatDateBR(pillEstimate!.range_end as string)}.`
    : 'Medicamentos (Vendido por Pill): entrega indisponível para este CEP.'

  if (isAllPillItems) return [pillText]

  // Carrinho misto (Pill + Cannect): as duas linhas aparecem SEMPRE juntas a partir daqui — nunca
  // esconder uma estimativa já pronta só porque a outra ainda não chegou. Bug original: qualquer
  // lado sem range fazia a função inteira devolver null, escondendo inclusive o lado que já tinha
  // o dado (ex.: Pill calcula mais rápido que Cannect, ou vice-versa).
  const othersHasRange = !!othersEstimate?.range_start && !!othersEstimate?.range_end
  const othersText = othersHasRange
    ? `Produtos Cannect: entrega estimada entre ${formatDateBR(
        othersEstimate!.range_start as string
      )} e ${formatDateBR(othersEstimate!.range_end as string)}.`
    : 'Produtos Cannect: calculando prazo de entrega.'

  return [othersText, pillText]
}

/**
 * Aviso de prazo para carrinho com item da Pill, usado enquanto ainda não há estimativa.
 *
 * O prazo da Pill depende do CEP — o backend só devolve `estimated_delivery_pill` depois que ele é
 * informado —, então os textos fixos de 25 e 15 dias úteis não valem aqui e enganariam o usuário.
 */
export const PILL_DELIVERY_CEP_TEXT = 'Consulte o CEP para saber o prazo de entrega.'

/**
 * Se o pedido tem ao menos um item da Pill.
 *
 * Prefere a flag do backend quando ela existe e cai para a origem do produto, mesmo critério que
 * `getCartSplitDeliveryEstimateTexts` usa. Basta **um** item: num carrinho misto o aviso do CEP
 * substitui os textos de prazo fixo, por decisão do time.
 */
export function orderHasPillItem(lastOrderCreated: any): boolean {
  if (typeof lastOrderCreated?.has_pill_items === 'boolean') return lastOrderCreated.has_pill_items
  const items = lastOrderCreated?.items || []
  return items.some((item: any) => item?.product?.origin === 'pill')
}

export function getCartSplitDeliveryEstimateTexts(lastOrderCreated: any): string[] | null {
  const items = lastOrderCreated?.items || []
  const hasPillItem = orderHasPillItem(lastOrderCreated)
  // Produto não-Pill tem `origin` null (Tiny/Cannect) — só a Pill marca 'pill'. Por isso o critério é
  // "todo item conhecido é Pill"; quando os itens vêm sem `product` (carrinho anônimo), a presença de
  // uma estimativa de "outros" com range já prova que o carrinho é misto.
  const othersEstimate = lastOrderCreated?.estimated_delivery_others
  const othersHasRange = !!othersEstimate?.range_start && !!othersEstimate?.range_end
  const itemsHaveProductInfo = items.some((item: any) => !!item?.product)
  const hasNonPillItem = items.some((item: any) => item?.product && item?.product?.origin !== 'pill')
  const isAllPillItems =
    items.length > 0 && hasPillItem && (itemsHaveProductInfo ? !hasNonPillItem : !othersHasRange)

  return getSplitDeliveryEstimateTexts({
    hasPillItem,
    isAllPillItems,
    pillEstimate: lastOrderCreated?.estimated_delivery_pill,
    othersEstimate
  })
}
